import React, { useState } from "react";
import styled from "styled-components";
import { Modal } from "@mantine/core"; // Import Mantine Modal
import { IconCheck } from "@tabler/icons-react"; // For the checkmark icon
import { supabase } from "../config/supabaseClient";

// Wrapper for the contact section
const ContactWrapper = styled.section`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-around;
  padding: 4rem;
  background-color: white;

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 2rem;
  }
`;

// Form wrapper
const FormWrapper = styled.div`
  max-width: 450px;
`;

// Title
const Title = styled.h1`
  font-size: 32px;
  margin-bottom: 0;
  color: #000;
  font-family: "Paris Avenue";
`;

// Description
const Description = styled.p`
  font-size: 16px;
  margin-bottom: 0;
  color: #555;
`;

// Contact Methods (links to chat, email, etc.)
const ContactMethods = styled.div`
  margin-bottom: 0.5rem;

  a {
    display: block;
    margin-bottom: 0.5rem;
    font-size: 16px;
    color: #e2c7a6;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

// Input fields style
const InputField = styled.input`
  width: 100%;
  padding: 0.75rem;
  margin-bottom: 1rem;
  border: 1px solid #ccc;
  border-radius: 5px;
  font-size: 16px;
  height: 40px;

  &:focus {
    border-color: #e2c7a6;
    outline: none;
  }
`;

// Text area field style for the message
const TextAreaField = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  margin-bottom: 1rem;
  border: 1px solid #ccc;
  border-radius: 5px;
  font-size: 16px;
  resize: none;
  height: 80px;

  &:focus {
    border-color: #e2c7a6;
    outline: none;
  }
`;

// Dropdown select style
const SelectField = styled.select`
  width: 100%;
  padding: 0.75rem;
  margin-bottom: 1rem;
  border: 1px solid #ccc;
  border-radius: 5px;
  font-size: 12px;
  height: 40px;

  &:focus {
    border-color: #e2c7a6;
    outline: none;
  }
`;

// Services checkboxes
const ServicesSection = styled.div`
  margin-bottom: 2rem;
  display: flex;
  justify-content: space-between;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  font-size: 14px;
  margin-bottom: 1rem;

  input {
    margin-right: 1rem;
  }
`;

const MapWrapper = styled.div`
  width: 100%;
  max-width: 500px;
  height: 550px; // Adjusted for smaller screens
  background: white; // Placeholder background for map
  border-radius: 5px;
  padding-top: 1rem;
  margin-top: 2rem;

  @media (max-width: 868px) {
    width: 100%;
    height: 400px; // Further reduction for better fit on smaller devices
    margin-top: 2rem;
  }
`;

// Submit button
const SubmitButton = styled.button`
  background-color: #e2c7a6;
  color: #fff;
  border: none;
  padding: 1rem 2rem;
  border-radius: 5px;
  font-size: 16px;
  cursor: pointer;

  &:hover {
    background-color: #bc9f8b;
  }
`;

// Modal
const StyledModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;
  background-color: #fff;
  border-radius: 20px;
  box-shadow: none; /* Removed shadow */
  text-align: center;
  position: relative;
`;

// Success Icon Styling
const SuccessIcon = styled(IconCheck)`
  color: #6bc47d;
  font-size: 48px;
  margin-bottom: 1.5rem;
`;

// Title Styling
const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: bold;
  color: #333;
  margin-bottom: 0.5rem;
`;

// Description Styling
const ModalDescription = styled.p`
  font-size: 16px;
  color: #777;
  margin-bottom: 1.5rem;
`;

// Green Background Styling for the entire screen
const ModalBackground = styled(Modal)`
  .mantine-Modal-content {
    background-color: transparent; // Make the background transparent
    border-radius: 20px;
    box-shadow: none; // Removed shadow
  }
  .mantine-Modal-close {
    background-color: white; // Transparent background for the close button
    color: #ff6961;
    z-index: 1;
    border-radius: 50%;
    position: absolute;
    top: 2.2rem;
  }

  .mantine-Modal-header {
    background-color: transparent;
  }
`;

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    number: "",
    message: "", // For the message input
    role: "", // For the select input
    services: [],
  });
  const [modalOpened, setModalOpened] = useState(false); // Modal state for thank you popup

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCheckboxChange = (e) => {
    const { checked, value } = e.target;
    setFormData((prevData) => {
      const updatedServices = checked
        ? [...prevData.services, value]
        : prevData.services.filter((service) => service !== value);
      return { ...prevData, services: updatedServices };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Submit data to Supabase "getintouch" table
      const { data, error } = await supabase.from("getintouch").insert([
        {
          name: formData.name,
          email: formData.email,
          number: formData.number,
          role: formData.role,
          message: formData.message,
          services: formData.services, // Supabase handles arrays for JSON columns
        },
      ]);

      if (error) throw error;

      // Clear the form after successful submission
      setFormData({
        name: "",
        email: "",
        number: "",
        message: "",
        role: "",
        services: [],
      });

      // Open the thank you modal
      setModalOpened(true);
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("There was an error submitting your message. Please try again.");
    }
  };

  return (
    <>
      <ContactWrapper id="contactus">
        <FormWrapper>
          <Title>Get in touch</Title>
          <form onSubmit={handleSubmit}>
            <InputField
              type="text"
              name="name"
              placeholder="Name"
              value={formData.name}
              onChange={handleChange}
            />
            <InputField
              type="email"
              name="email"
              placeholder="Email"
              value={formData.email}
              onChange={handleChange}
            />
            <InputField
              type="tel"
              name="number"
              placeholder="+971 (555) 00-0000"
              value={formData.number}
              onChange={handleChange}
            />

            {/* New Role Selector */}
            <SelectField
              name="role"
              value={formData.role}
              onChange={handleChange}
            >
              <option value="" disabled>
                Who are you?
              </option>
              <option value="Customer">Customer</option>
              <option value="Broker">Broker</option>
              <option value="Investor">Investor</option>
              <option value="Tenant">Tenant</option>
              <option value="Property Owner">Property Owner</option>
              <option value="Other">Other</option>
            </SelectField>

            {/* New TextArea for Message */}
            <TextAreaField
              name="message"
              placeholder="Enter your message here..."
              rows={4}
              value={formData.message}
              onChange={handleChange}
            />

            {/* Services Checkboxes */}
            <ServicesSection>
              <div>
                <CheckboxLabel>
                  <input
                    type="checkbox"
                    value="Brokerage Solutions"
                    onChange={handleCheckboxChange}
                    checked={formData.services.includes("Brokerage Solutions")}
                  />
                  Brokerage Solutions
                </CheckboxLabel>
                <CheckboxLabel>
                  <input
                    type="checkbox"
                    value="Property Advisory"
                    onChange={handleCheckboxChange}
                    checked={formData.services.includes("Property Advisory")}
                  />
                  Property Advisory
                </CheckboxLabel>
                <CheckboxLabel>
                  <input
                    type="checkbox"
                    value="Market Analysis"
                    onChange={handleCheckboxChange}
                    checked={formData.services.includes("Market Analysis")}
                  />
                  Market Analysis
                </CheckboxLabel>
              </div>

              <div>
                <CheckboxLabel>
                  <input
                    type="checkbox"
                    value="Legal and Documentation Assistance"
                    onChange={handleCheckboxChange}
                    checked={formData.services.includes(
                      "Legal and Documentation Assistance"
                    )}
                  />
                  Legal and Documentation Assistance
                </CheckboxLabel>
                <CheckboxLabel>
                  <input
                    type="checkbox"
                    value="Content Creation"
                    onChange={handleCheckboxChange}
                    checked={formData.services.includes("Content Creation")}
                  />
                  Content Creation
                </CheckboxLabel>
                <CheckboxLabel>
                  <input
                    type="checkbox"
                    value="Strategy & Consulting"
                    onChange={handleCheckboxChange}
                    checked={formData.services.includes(
                      "Strategy & Consulting"
                    )}
                  />
                  Strategy & Consulting
                </CheckboxLabel>
              </div>
            </ServicesSection>

            <SubmitButton type="submit">Submit</SubmitButton>
          </form>
        </FormWrapper>

        <MapWrapper>
          {/* Embed Google Map here */}
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d798.0035800595699!2d55.29604374044558!3d25.24102084301507!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f43dfcf271a1d%3A0xa95c64131d63078f!2sEmbark%20Properties!5e0!3m2!1sen!2sae!4v1738758484034!5m2!1sen!2sae"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          ></iframe>
        </MapWrapper>
      </ContactWrapper>

      {/* Modal for submission success */}
      <ModalBackground
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
      >
        <StyledModalContent>
          <SuccessIcon />
          <ModalTitle>Thank You!</ModalTitle>
          <ModalDescription>
            Your response has been recorded successfully. We will get in touch
            soon.
          </ModalDescription>
        </StyledModalContent>
      </ModalBackground>
    </>
  );
};

export default ContactUs;
